import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Form, Row, Col, Modal, Spinner, Button } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import { IMaskInput } from "react-imask";
import CNPJ, { validarCNPJ } from "../Generals/Inputs/CNPJ";
import { FaCog } from "react-icons/fa";
import { GlobalContext } from "../../Utils/GlobalContext";
import { Salvar, Cancelar } from "../Generals/buttons";
import EmpresasConfiguracoesModal from "./EmpresasConfiguracoesModal";
import TiposFornecedorModal from "../TiposFornecedor/TiposFornecedorModal";
import "./EmpresasModal.css";


// Campos que ocupam metade da largura (4 por linha) para reduzir o tamanho da tela
const CAMPOS_REDUZIDOS = [
  "cep",
  "uf",
  "telefone_celular",
  "telefone_fixo",
  "insc_estadual",
  "situacao",
  "id_regime_tributario",
];

const ORDEM_CAMPOS_ENDERECO = [
  "cep",
  "endereco",
  "complemento",
  "bairro",
  "uf",
  "id_cidade",
];

function normalizarTextoBusca(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function validarCPF(cpf) {
  const nums = String(cpf || "").replace(/\D/g, "");
  if (nums.length !== 11) return false;
  if (/^(\d)\1+$/.test(nums)) return false;

  const calc = (digits, pesoInicial) => {
    const soma = digits.reduce((acc, d, i) => acc + d * (pesoInicial - i), 0);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  const d = nums.split("").map(Number);
  return d[9] === calc(d.slice(0, 9), 10) && d[10] === calc(d.slice(0, 10), 11);
}

const EmpresasModal = ({ show, handleClose, empresa, fetchEmpresas, classificacaoOverride }) => {
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  // classificacaoOverride: usado por quem embute este modal fora da tela
  // Empresas.js (ex.: assistente de Auto Configuração) para não depender do
  // localStorage/contexto global — evita alterar a classificação vista pela
  // tela por trás do assistente enquanto o modal estiver aberto.
  const classificacao =
    classificacaoOverride || globalData?.classificacao || localStorage.getItem("classificacao");

  const [formValues, setFormValues] = useState({
    razao_social: "",
    fantasia: "",
    cnpj: "",
    cpf: "",
    tipo_pessoa: "PJ",
    endereco: "",
    telefone_fixo: "",
    telefone_celular: "",
    situacao: "A",
    cep: "",
    bairro: "",
    email: "",
    email_cobranca: "",
    email_receita_federal: "",
    id_regime_tributario: "",
    id_cidade: "",
    id_tipo_fornecedor: "",
    insc_estadual: "",
    complemento: "",
  });

  const [cidades, setCidades] = useState([]);
  const [ufs, setUfs] = useState([]);
  const [uf, setUf] = useState("");
  const [configuracoesCampos, setConfiguracoesCampos] = useState([]);
  const [regimesTributarios, setRegimesTributarios] = useState([]);
  const [tiposFornecedor, setTiposFornecedor] = useState([]);
  const [showTipoFornecedorModal, setShowTipoFornecedorModal] = useState(false);
  const idsTiposFornecedorAntesCadastroRef = useRef(new Set());
  const [loading, setLoading] = useState(true);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjError, setCnpjError] = useState("");
  const [error, setError] = useState(null);
  const [showConfiguracoes, setShowConfiguracoes] = useState(false);

  // ✅ Deriva o ID do tipo de configuração a partir da classificação de forma MEMOIZADA
  const idTipoConfiguracao = useMemo(() => {
    if (classificacao === "E") return 1;
    if (classificacao === "F") return 2;
    if (classificacao === "V") return 3;
    return null;
  }, [classificacao]);

  // Carrega as configurações dos campos (depende apenas do ID memoizado)
  const fetchConfiguracoesCampos = useCallback(async () => {
    if (!idTipoConfiguracao) {
      console.error("Tipo de configuração não identificado");
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(
        `${BASE_URL}/api/configuracaoCampos/ListarConfiguracoesCamposPorTipoEmpresa/${idTipoConfiguracao}`
      );

      if (response.data?.data && Array.isArray(response.data.data)) {
        setConfiguracoesCampos(response.data.data);
      } else {
        console.error("Formato de dados inválido para configurações de campos", response.data);
        setError("Erro ao carregar configurações de campos");
      }
    } catch (error) {
      console.error("Erro ao buscar configurações de campos:", error);
      setError("Erro ao carregar configurações de campos");
    } finally {
      setLoading(false);
    }
  }, [idTipoConfiguracao]);

  const resetForm = useCallback(() => {
    setFormValues({
      id_entidade: entidadeID,
      razao_social: "",
      fantasia: "",
      cnpj: "",
      cpf: "",
      tipo_pessoa: "PJ",
      endereco: "",
      telefone_fixo: "",
      telefone_celular: "",
      situacao: "A",
      cep: "",
      bairro: "",
      email: "",
      email_cobranca: "",
      email_receita_federal: "",
      id_regime_tributario: "",
      id_cidade: "",
      id_tipo_fornecedor: "",
      insc_estadual: "",
      complemento: "",
    });
    setUf("");
    setCidades([]);
    setCepError("");
    setCnpjError("");
  }, [entidadeID]);

  const recarregarTiposFornecedor = useCallback(async (idTipoFornecedorPreferencial = null, selecionarCriado = false) => {
    if (!entidadeID) {
      setTiposFornecedor([]);
      return [];
    }
    try {
      const response = await api.get(`${BASE_URL}/api/tiposEmpresa/ListarTiposEmpresa/${entidadeID}`);
      const lista = Array.isArray(response.data?.data) ? response.data.data : [];
      setTiposFornecedor(lista);

      const tipoNovo = selecionarCriado
        ? lista.find((tipo) => !idsTiposFornecedorAntesCadastroRef.current.has(String(tipo.id)))
        : null;
      const idParaSelecionar = idTipoFornecedorPreferencial ?? tipoNovo?.id;
      if (idParaSelecionar != null) {
        setFormValues((prev) => ({
          ...prev,
          id_tipo_fornecedor: String(idParaSelecionar),
        }));
      }

      idsTiposFornecedorAntesCadastroRef.current = new Set(
        lista.map((tipo) => String(tipo.id))
      );
      return lista;
    } catch (error) {
      console.error("Erro ao buscar tipos de fornecedor:", error);
      setTiposFornecedor([]);
      return [];
    }
  }, [entidadeID]);

  const abrirCadastroTipoFornecedor = useCallback(() => {
    idsTiposFornecedorAntesCadastroRef.current = new Set(
      tiposFornecedor.map((tipo) => String(tipo.id))
    );
    setShowTipoFornecedorModal(true);
  }, [tiposFornecedor]);

  const handleCloseTipoFornecedorModal = useCallback(async (resultado) => {
    setShowTipoFornecedorModal(false);
    const idCriado = resultado?.id ?? resultado?.id_tipo_fornecedor ?? resultado?.id_tipo_empresa;
    await recarregarTiposFornecedor(idCriado != null ? String(idCriado) : null, true);
  }, [recarregarTiposFornecedor]);

  // Inicializa os dados quando o modal é aberto
  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);

      const loadAllData = async () => {
        try {
          await fetchConfiguracoesCampos();

          const [ufsResponse, regimesTributariosResponse] = await Promise.all([
            api.get(`${BASE_URL}/api/enderecoDados/ufs`),
            api.get(`${BASE_URL}/api/empresas/regimesTributarios`),
          ]);
          setUfs(ufsResponse.data);
          setRegimesTributarios(regimesTributariosResponse.data.data);

          await recarregarTiposFornecedor();

          if (empresa) {
            await loadEmpresaData(empresa);
          } else {
            resetForm();
          }
        } catch (error) {
          console.error("Erro ao carregar dados iniciais:", error);
          setError("Ocorreu um erro ao carregar os dados necessários");
        } finally {
          setLoading(false);
        }
      };

      loadAllData();
    }
  }, [show, empresa, entidadeID, fetchConfiguracoesCampos, resetForm, recarregarTiposFornecedor]);

  // Carrega os dados da empresa para edição
  const loadEmpresaData = async (empresa) => {
    setFormValues({
      razao_social: empresa.razao_social || "",
      fantasia: empresa.fantasia || "",
      cnpj: empresa.cnpj || "",
      cpf: empresa.cpf || "",
      tipo_pessoa: empresa.tipo_pessoa || (empresa.cpf ? "PF" : "PJ"),
      endereco: empresa.endereco || "",
      telefone_fixo: empresa.telefone_fixo || "",
      telefone_celular: empresa.telefone_celular || "",
      situacao: empresa.situacao || "A",
      cep: empresa.cep || "",
      bairro: empresa.bairro || "",
      email: empresa.email || "",
      email_cobranca: empresa.email_cobranca || "",
      email_receita_federal: empresa.email_receita_federal || "",
      id_regime_tributario: empresa.id_regime_tributario || "",
      id_cidade: empresa.id_cidade || "",
      id_tipo_fornecedor: empresa.id_tipo_fornecedor || "",
      insc_estadual: empresa.insc_estadual || "",
      complemento: empresa.complemento || "",
    });

    if (empresa.id_cidade) {
      try {
        const ufResponse = await api.get(
          `${BASE_URL}/api/enderecoDados/ufPorCidade/${empresa.id_cidade}`
        );
        const UFdaCidade = ufResponse.data.uf;
        const codIbge    = ufResponse.data.cod_ibge;
        setUf(UFdaCidade);

        const cidadesResponse = await api.get(
          `${BASE_URL}/api/enderecoDados/cidades/${UFdaCidade}`
        );
        setCidades(cidadesResponse.data);

        // O dropdown usa cod_ibge como value; ajusta formValues para coincidir
        if (codIbge) {
          setFormValues((prev) => ({ ...prev, id_cidade: String(codIbge) }));
        }
      } catch (error) {
        console.error("Erro ao buscar UF ou Cidades pela cidade ID:", error);
      }
    }
  };

  const buscarCidadesPorUf = useCallback(async (ufSelecionada) => {
    if (!ufSelecionada) {
      setCidades([]);
      return [];
    }

    const response = await api.get(
      `${BASE_URL}/api/enderecoDados/cidades/${ufSelecionada.toUpperCase()}`
    );
    const lista = Array.isArray(response.data) ? response.data : [];
    setCidades(lista);
    return lista;
  }, []);

  const encontrarCidadeCep = (listaCidades, nomeCidade, codIbge) => {
    const cidadeNormalizada = normalizarTextoBusca(nomeCidade);
    const codIbgeNormalizado = codIbge ? String(codIbge) : "";

    return (
      listaCidades.find((cidade) => codIbgeNormalizado && String(cidade.cod_ibge || cidade.id) === codIbgeNormalizado) ||
      listaCidades.find((cidade) => normalizarTextoBusca(cidade.nome) === cidadeNormalizada) ||
      listaCidades.find((cidade) =>
        cidadeNormalizada &&
        normalizarTextoBusca(cidade.nome).includes(cidadeNormalizada)
      )
    );
  };

  const consultarCep = useCallback(async () => {
    const cepNumbers = String(formValues.cep || "").replace(/\D/g, "");

    if (!cepNumbers) {
      setCepError("");
      return;
    }

    if (cepNumbers.length !== 8) {
      setCepError("CEP deve conter 8 digitos.");
      return;
    }

    setCepLoading(true);
    setCepError("");

    try {
      const response = await api.get(`${BASE_URL}/api/enderecoDados/cep/${cepNumbers}`);
      const dadosCep = response.data || {};
      const ufCep = String(dadosCep.uf || "").toUpperCase();
      const listaCidades = ufCep ? await buscarCidadesPorUf(ufCep) : [];
      const cidadeEncontrada = encontrarCidadeCep(listaCidades, dadosCep.municipio, dadosCep.ibge);

      setUf(ufCep);
      setFormValues((prev) => ({
        ...prev,
        endereco:  dadosCep.logradouro || "",
        bairro:    dadosCep.bairro     || "",
        uf:        ufCep,
        id_cidade: cidadeEncontrada ? String(cidadeEncontrada.id) : "",
      }));
    } catch (error) {
      if (error.response?.status === 404) {
        setCepError("CEP não encontrado.");
      } else {
        setCepError("Erro ao consultar CEP. Verifique a conexão.");
      }
      setFormValues((prev) => ({
        ...prev,
        endereco: "",
        bairro: "",
        uf: "",
        id_cidade: "",
      }));
      setUf("");
      setCidades([]);
    } finally {
      setCepLoading(false);
    }
  }, [buscarCidadesPorUf, formValues.cep]);

  const limparCamposConsultaCnpj = useCallback(() => {
    setUf("");
    setCidades([]);
    setFormValues((prev) => ({
      ...prev,
      razao_social: "",
      fantasia: "",
      cep: "",
      endereco: "",
      complemento: "",
      bairro: "",
      email_receita_federal: "",
      uf: "",
      id_cidade: "",
    }));
  }, []);

  const consultarCnpjEmpresa = useCallback(async () => {
    const cnpjNumbers = String(formValues.cnpj || "").replace(/\D/g, "");

    if (!["F", "V"].includes(classificacao) || formValues.tipo_pessoa === "PF") {
      return;
    }

    if (!cnpjNumbers) {
      setCnpjError("");
      return;
    }

    if (cnpjNumbers.length !== 14) {
      setCnpjError("CNPJ deve conter 14 digitos.");
      return;
    }

    if (!validarCNPJ(formValues.cnpj)) {
      setCnpjError("CNPJ invalido. Verifique os digitos informados.");
      return;
    }

    limparCamposConsultaCnpj();
    setCnpjLoading(true);
    setCnpjError("");

    try {
      const response = await api.get(`${BASE_URL}/api/empresas/consultarCNPJ/${cnpjNumbers}`);
      const dados = response.data?.data || {};
      const ufReceita = String(dados.uf || "").toUpperCase();
      const cidadeReceita = dados.cidade || "";
      const listaCidades = ufReceita ? await buscarCidadesPorUf(ufReceita) : [];
      const cidadeEncontrada = encontrarCidadeCep(listaCidades, cidadeReceita);

      if (listaCidades.length > 0) {
        setCidades(listaCidades);
      }

      if (ufReceita) {
        setUf(ufReceita);
      }

      setFormValues((prev) => ({
        ...prev,
        razao_social: dados.razao_social || "",
        fantasia: dados.fantasia || "",
        cep: dados.cep || "",
        endereco: dados.endereco || "",
        complemento: dados.complemento || "",
        bairro: dados.bairro || "",
        email_receita_federal: dados.email_receita_federal || "",
        uf: ufReceita,
        id_cidade: cidadeEncontrada ? String(cidadeEncontrada.id) : "",
      }));
    } catch (error) {
      console.error("Erro ao consultar CNPJ:", error);
      setCnpjError(error.response?.data?.message || "Nao foi possivel consultar o CNPJ informado.");
    } finally {
      setCnpjLoading(false);
    }
  }, [buscarCidadesPorUf, classificacao, formValues.cnpj, formValues.tipo_pessoa, limparCamposConsultaCnpj]);

  // Atualiza lista de cidades quando UF muda (em modo de novo cadastro)
  useEffect(() => {
    if (uf && !empresa?.id_cidade && !formValues.id_cidade) {
      const fetchCidadesByUf = async () => {
        try {
          await buscarCidadesPorUf(uf);
        } catch (error) {
          console.error("Erro ao buscar cidades:", error);
        }
      };
      fetchCidadesByUf();
    }
  }, [uf, empresa, formValues.id_cidade, buscarCidadesPorUf]);

  // Verifica campos obrigatórios
  const verificarCamposObrigatorios = () => {
    let camposFaltantes = [];
    const pessoaFisica = classificacao === "F" && formValues.tipo_pessoa === "PF";

    configuracoesCampos.forEach((campoConfig) => {
      const { nome_campo, obrigatorio, visivel, descricao } = campoConfig;
      if (classificacao === "F" && ["cnpj", "cpf", "tipo_pessoa"].includes(nome_campo)) {
        return;
      }
      // Pessoa Física não exibe nome fantasia — não pode exigi-lo.
      if (pessoaFisica && nome_campo === "fantasia") {
        return;
      }

      if (visivel === "S" && obrigatorio === "S" && !formValues[nome_campo]?.toString().trim()) {
        camposFaltantes.push(pessoaFisica && nome_campo === "razao_social" ? "Nome" : (descricao || nome_campo));
      }
    });

    return camposFaltantes;
  };

  // Salva a empresa
  const handleSave = async () => {
    if (!configuracoesCampos || configuracoesCampos.length === 0) {
      setError("Configurações de campos não foram carregadas.");
      return;
    }

    const camposFaltantes = verificarCamposObrigatorios();
    if (camposFaltantes.length > 0) {
      setError("Os seguintes campos obrigatórios não foram preenchidos: " + camposFaltantes.join(", ") + ".");
      return;
    }

    const tipoPessoa = formValues.tipo_pessoa || "PJ";
    const cnpj = formValues.cnpj || "";
    const cpf = formValues.cpf || "";

    if (classificacao === "F" && tipoPessoa === "PF") {
      if (!cpf.replace(/\D/g, "")) {
        setError("CPF e obrigatorio para fornecedor pessoa fisica.");
        return;
      }
      if (!validarCPF(cpf)) {
        setError("CPF invalido. Verifique os digitos informados.");
        return;
      }
    } else if (cnpj && cnpj.replace(/\D/g, "").length > 0 && !validarCNPJ(cnpj)) {
      setError("CNPJ invalido. Verifique os digitos informados.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "id_entidade": entidadeID,
      };

      const requestBody = {
        ...formValues,
        classificacao, // 🔹 vem do contexto
        uf,            // necessário para resolver id_cidade (código IBGE → id interno)
      };

      if (!requestBody.email_receita_federal) {
        delete requestBody.email_receita_federal;
      }

      if (classificacao === "F") {
        requestBody.tipo_pessoa = tipoPessoa;
        if (tipoPessoa === "PF") {
          requestBody.cpf = cpf;
          requestBody.cnpj = null;
        } else {
          requestBody.cnpj = cnpj;
          requestBody.cpf = null;
        }
      }

      let empresaIdResultado;
      if (empresa) {
        await api.put(
          `${BASE_URL}/api/empresas/EditarEmpresas/${empresa.id}`,
          requestBody,
          { headers }
        );
        empresaIdResultado = empresa.id;
      } else {
        const response = await api.post(
          `${BASE_URL}/api/empresas/InserirEmpresas/${entidadeID}`,
          requestBody,
          { headers }
        );
        // Se a resposta contém o ID da empresa criada, usa ele
        empresaIdResultado = response.data?.id || response.data?.insertId;
      }

      if (!empresa && empresaIdResultado) {
        // Aplica configurações temporárias (se houver)
        const configTemp = localStorage.getItem(`empresa_config_temp_${entidadeID}`);
        if (configTemp) {
          try {
            const configData = JSON.parse(configTemp);
            await api.put(
              `${BASE_URL}/api/empresasConfiguracoes/editarConfiguracoes/${empresaIdResultado}`,
              configData,
              { headers }
            );
            localStorage.removeItem(`empresa_config_temp_${entidadeID}`);
          } catch (error) {
            console.error("Erro ao aplicar configurações temporárias:", error);
          }
        }
        // Dispara o fluxo ConfigCampos → GruposAcesso no componente pai
        handleClose({
          id: empresaIdResultado,
          nome: formValues.fantasia || formValues.razao_social,
          idTipoConfiguracao,
        });
      } else {
        fetchEmpresas();
        handleClose();
      }
    } catch (err) {
      console.error("Erro ao salvar empresa:", err);
      setError("Erro ao salvar empresa. Verifique os dados e tente novamente.");
      if (err.response?.data?.message) {
        setError(`Erro ao salvar empresa: ${err.response.data.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Renderização dinâmica de campos
  const renderCamposVisiveis = () => {
    if (loading) {
      return (
        <Col xs={12} className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
        </Col>
      );
    }

    if (error) {
      return (
        <>
          <Col xs={12} className="mb-3">
            <div className="alert alert-danger">{error}</div>
          </Col>
          {renderCampos()}
        </>
      );
    }

    return renderCampos();
  };

  const renderCampos = () => {
    if (!configuracoesCampos || configuracoesCampos.length === 0) {
      return (
        <Col xs={12} className="text-center py-4">
          <div className="alert alert-warning">
            Nenhuma configuração de campo disponível para esta classificação.
          </div>
        </Col>
      );
    }

    const pessoaFisica = classificacao === "F" && formValues.tipo_pessoa === "PF";

    const camposVisiveisBase = configuracoesCampos
      .filter((campoConfig) =>
        campoConfig.visivel === "S" &&
        !(classificacao === "F" && campoConfig.nome_campo === "id_tipo_fornecedor") &&
        !(classificacao === "F" && ["cnpj", "cpf", "tipo_pessoa"].includes(campoConfig.nome_campo)) &&
        !(classificacao === "V" && campoConfig.nome_campo === "cnpj") &&
        // Pessoa Física não tem nome fantasia — só razão social (usada como "Nome").
        !(pessoaFisica && campoConfig.nome_campo === "fantasia")
      );
    const ordensEndereco = camposVisiveisBase
      .filter((campoConfig) => ORDEM_CAMPOS_ENDERECO.includes(campoConfig.nome_campo))
      .map((campoConfig) => Number(campoConfig.ordem) || 0);
    const primeiraOrdemEndereco = ordensEndereco.length ? Math.min(...ordensEndereco) : null;

    const camposVisiveis = camposVisiveisBase.sort((a, b) => {
      const enderecoA = ORDEM_CAMPOS_ENDERECO.indexOf(a.nome_campo);
      const enderecoB = ORDEM_CAMPOS_ENDERECO.indexOf(b.nome_campo);
      const ordemA = enderecoA >= 0 && primeiraOrdemEndereco !== null
        ? primeiraOrdemEndereco + enderecoA / 100
        : Number(a.ordem) || 0;
      const ordemB = enderecoB >= 0 && primeiraOrdemEndereco !== null
        ? primeiraOrdemEndereco + enderecoB / 100
        : Number(b.ordem) || 0;

      return ordemA - ordemB;
    });

    if (camposVisiveis.length === 0) {
      return (
        <Col xs={12} className="text-center py-4">
          <div className="alert alert-info">
            Não há campos configurados como visíveis para esta classificação.
          </div>
        </Col>
      );
    }

    return camposVisiveis.map((campoConfig) => {
      const nomeCampo = campoConfig.nome_campo;
      // Pessoa Física registra o nome da pessoa no mesmo campo razao_social —
      // só o rótulo muda para "Nome" (sem nome fantasia separado).
      const labelCampo = pessoaFisica && nomeCampo === "razao_social"
        ? "Nome"
        : campoConfig.descricao || nomeCampo;
      const isRequired = campoConfig.obrigatorio === "S";
      const reduzido = CAMPOS_REDUZIDOS.includes(nomeCampo);
      const colSize = reduzido ? 3 : 6; // reduzido: 4 por linha (12/3); demais: 2 por linha (12/6)

      return (
        <Col md={colSize} key={nomeCampo} className="mb-3">
          <Form.Group controlId={nomeCampo}>
            <Form.Label>
              {labelCampo}
              {isRequired && <span className="text-danger ms-1">*</span>}
            </Form.Label>
            {renderCampoInput(nomeCampo, labelCampo)}
            {nomeCampo === "cnpj" && empresa && classificacao === "E" && (
              <Form.Text muted>Não é possível alterar o CNPJ após o cadastro da entidade.</Form.Text>
            )}
          </Form.Group>
        </Col>
      );
    });
  };

  const renderCampoInput = (nomeCampo, labelCampo) => {
    switch (nomeCampo) {
      case "id_tipo_fornecedor":
        return (
          <Form.Select
            value={formValues[nomeCampo] || ""}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
            disabled={classificacao === "E"}
          >
            <option value="">Selecione uma opção</option>
            {tiposFornecedor.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.descricao}
              </option>
            ))}
          </Form.Select>
        );

      case "cnpj": {
        // Entidade (classificacao='E') já cadastrada: CNPJ não pode mais ser alterado
        // pela tela (é o identificador legal usado na cobrança/integração Asaas).
        const cnpjBloqueado = Boolean(empresa) && classificacao === "E";
        return (
          <CNPJ
            id={nomeCampo}
            value={formValues[nomeCampo] || ""}
            onChange={(value) => handleInputChange(nomeCampo, value)}
            disabled={cnpjBloqueado}
          />
        );
      }

      case "cep":
        return (
          <>
            <div className="position-relative">
              <IMaskInput
                id="cep"
                mask="00000-000"
                value={formValues[nomeCampo] || ""}
                onAccept={(value) => {
                  setCepError("");
                  handleInputChange(nomeCampo, value);
                }}
                onBlur={consultarCep}
                unmask={false}
                inputMode="numeric"
                placeholder="Informe o CEP"
                className={`form-control ${cepError ? "is-invalid" : ""}`}
              />
              {cepLoading && (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ position: "absolute", right: 10, top: 10 }}
                />
              )}
            </div>
            {cepError && <div className="invalid-feedback d-block">{cepError}</div>}
          </>
        );

      case "telefone_fixo":
        return (
          <IMaskInput
            mask="(00) 0000-0000"
            value={formValues[nomeCampo] || ""}
            onAccept={(value) => handleInputChange(nomeCampo, value)}
            unmask={false}
            inputMode="numeric"
            placeholder="Informe o telefone fixo"
            className="form-control"
          />
        );

      case "telefone_celular":
        return (
          <IMaskInput
            mask="(00) 00000-0000"
            value={formValues[nomeCampo] || ""}
            onAccept={(value) => handleInputChange(nomeCampo, value)}
            unmask={false}
            inputMode="numeric"
            placeholder="Informe o telefone celular"
            className="form-control"
          />
        );

      case "email":
      case "email_cobranca":
        return (
          <Form.Control
            type="email"
            value={formValues[nomeCampo] || ""}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
            placeholder={`Informe o email`}
          />
        );

      case "uf":
        return (
          <Form.Select
            value={uf || ""}
            onChange={async (e) => {
              const selectedUf = e.target.value;
              setUf(selectedUf);
              setFormValues((prev) => ({ ...prev, id_cidade: "" }));
              if (selectedUf) {
                try {
                  await buscarCidadesPorUf(selectedUf);
                } catch (error) {
                  console.error("Erro ao buscar cidades:", error);
                }
              } else {
                setCidades([]);
              }
            }}
          >
            <option value="">Selecione o estado</option>
            {ufs.map((estado) => (
              <option key={estado.sigla} value={estado.sigla}>
                {estado.nome}
              </option>
            ))}
          </Form.Select>
        );

      case "id_cidade":
        return (
          <Form.Select
            value={formValues.id_cidade || ""}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
            disabled={!uf}
          >
            <option value="">Selecione a cidade</option>
            {cidades.map((cidade) => (
              <option key={cidade.id} value={cidade.id}>
                {cidade.nome}
              </option>
            ))}
          </Form.Select>
        );

      case "id_regime_tributario":
        return (
          <Form.Select
            value={formValues.id_regime_tributario || ""}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
          >
            <option value="">Selecione o regime tributário</option>
            {regimesTributarios.map((regime) => (
              <option key={regime.id} value={regime.id}>
                {regime.descricao}
              </option>
            ))}
          </Form.Select>
        );

      case "situacao":
        return (
          <Form.Select
            value={formValues.situacao || "A"}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
          >
            <option value="A">Ativo</option>
            <option value="I">Inativo</option>
          </Form.Select>
        );

      default:
        return (
          <Form.Control
            type="text"
            value={formValues[nomeCampo] || ""}
            onChange={(e) => handleInputChange(nomeCampo, e.target.value)}
            placeholder={`Informe ${labelCampo}`}
          />
        );
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "tipo_pessoa") {
      setCnpjError("");
      setFormValues((prev) => ({
        ...prev,
        tipo_pessoa: value,
        cpf: value === "PF" ? prev.cpf : "",
        cnpj: value === "PJ" ? prev.cnpj : "",
      }));
      return;
    }

    if (field === "cnpj") {
      setCnpjError("");
    }

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} backdrop="static" size="xl" dialogClassName="modal-dialog-top">
        <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>
            {empresa
              ? classificacao === "E" ? "Empresa Entidade"
              : classificacao === "V" ? "Filial"
              : "Empresa Fornecedor"
              : classificacao === "E" ? "Adicionar Empresa Entidade"
              : classificacao === "V" ? "Adicionar Filial"
              : "Adicionar Empresa Fornecedor"}
            {empresa?.razao_social && (
              <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 400, color: "var(--text-muted)", marginTop: 2 }}>
                {empresa.fantasia || empresa.razao_social}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
          <Form>
            <Row>
              {classificacao === "F" && (
                <>
                  <Col md={3} className="mb-3">
                    <Form.Group controlId="tipo_pessoa">
                      <Form.Label>Tipo de Pessoa</Form.Label>
                      <Form.Select
                        value={formValues.tipo_pessoa || "PJ"}
                        onChange={(e) => handleInputChange("tipo_pessoa", e.target.value)}
                      >
                        <option value="PJ">Pessoa Jurídica</option>
                        <option value="PF">Pessoa Física</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3} className="mb-3">
                    <Form.Group controlId={formValues.tipo_pessoa === "PF" ? "cpf" : "cnpj"}>
                      <Form.Label>{formValues.tipo_pessoa === "PF" ? "CPF" : "CNPJ"}</Form.Label>
                      {formValues.tipo_pessoa === "PF" ? (
                        <IMaskInput
                          id="cpf"
                          mask="000.000.000-00"
                          value={formValues.cpf || ""}
                          onAccept={(value) => handleInputChange("cpf", value)}
                          unmask={false}
                          inputMode="numeric"
                          placeholder="Informe o CPF"
                          className="form-control"
                        />
                      ) : (
                        <>
                          <div className="position-relative">
                            <CNPJ
                              id="cnpj"
                              value={formValues.cnpj || ""}
                              onChange={(value) => handleInputChange("cnpj", value)}
                              onBlur={consultarCnpjEmpresa}
                              className={`form-control ${cnpjError ? "is-invalid" : ""}`}
                            />
                            {cnpjLoading && (
                              <Spinner
                                animation="border"
                                size="sm"
                                style={{ position: "absolute", right: 10, top: 10 }}
                              />
                            )}
                          </div>
                          {cnpjError && <div className="invalid-feedback d-block">{cnpjError}</div>}
                        </>
                      )}
                    </Form.Group>
                  </Col>
                </>
              )}
              {classificacao === "V" && (
                <Col md={3} className="mb-3">
                  <Form.Group controlId="cnpj">
                    <Form.Label>CNPJ</Form.Label>
                    <div className="position-relative">
                      <CNPJ
                        id="cnpj"
                        value={formValues.cnpj || ""}
                        onChange={(value) => handleInputChange("cnpj", value)}
                        onBlur={consultarCnpjEmpresa}
                        className={`form-control ${cnpjError ? "is-invalid" : ""}`}
                      />
                      {cnpjLoading && (
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ position: "absolute", right: 10, top: 10 }}
                        />
                      )}
                    </div>
                    {cnpjError && <div className="invalid-feedback d-block">{cnpjError}</div>}
                  </Form.Group>
                </Col>
              )}
              {renderCamposVisiveis()}
              {classificacao === "F" && (
                <Col md={6} className="mb-3">
                  <Form.Group controlId="id_tipo_fornecedor">
                    <Form.Label>Tipo de Fornecedor</Form.Label>
                    <div className="em-select-add-combo">
                    <Form.Select
                      className="em-select-add-combo-select"
                      value={formValues.id_tipo_fornecedor || ""}
                      onChange={(e) => handleInputChange("id_tipo_fornecedor", e.target.value)}
                    >
                      <option value="">Selecione uma opção</option>
                      {tiposFornecedor.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.descricao}
                        </option>
                      ))}
                    </Form.Select>
                    <span
                      className="em-select-add-combo-button-wrapper"
                      title="Adicionar tipo de fornecedor"
                    >
                      <button
                        type="button"
                        className="em-select-add-combo-button"
                        onClick={abrirCadastroTipoFornecedor}
                        aria-label="Adicionar tipo de fornecedor"
                        title="Adicionar tipo de fornecedor"
                      >
                        <span className="em-select-add-action-circle" aria-hidden="true" />
                      </button>
                    </span>
                    </div>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda">
            {empresa && classificacao === "E" && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowConfiguracoes(true)}
                title="Configurações da Entidade"
              >
                <FaCog />
              </Button>
            )}
          </div>
          <div className="modal-footer-acoes-direita">
            <Cancelar onClick={handleClose} disabled={loading} />
            <Salvar onClick={handleSave} loading={loading} />
          </div>
        </Modal.Footer>
      </Modal>

      <EmpresasConfiguracoesModal
        show={showConfiguracoes}
        handleClose={() => setShowConfiguracoes(false)}
        empresaId={empresa?.id}
        entidadeID={entidadeID}
        empresaNome={empresa?.fantasia || empresa?.razao_social}
      />
      <TiposFornecedorModal
        show={showTipoFornecedorModal}
        handleClose={handleCloseTipoFornecedorModal}
        fornecedor={null}
        fetchTiposFornecedor={() => {}}
      />
    </>
  );
};

export default EmpresasModal;
